import { useCallback, useEffect } from "react";
import type { Socket } from "socket.io-client";
import io from "socket.io-client";

import { devLog } from "~/core/generic/common-utils";

import { socketConfigs } from "../configs";
import type { SocketEventData } from "../types";
import { SocketServiceEnum } from "../types";

type MessageCallback<T> = (data: SocketEventData<T>) => void;

type SocketConnectionParams<T> = {
  serviceName?: SocketServiceEnum;
  event: string;
  room?: string;
  messageCallback: MessageCallback<T>;
  onError?: (error: Error) => void;
};

const socketInstances = new Map<string, Socket>();

/**
 * @description
 * This hook is used to connect to a socket server and listen for events.
 * It will automatically reconnect if the connection is lost.
 * It will also join a room if a room is provided.
 * It will also listen for events and call the messageCallback with the event data.
 * It will also listen for errors and call the onError callback with the error.
 * @example
 * ```tsx
 * useSocketConnection({
 *   event: SocketEvents.opportunity.listUpdated,
 *   room: `${SocketEvents.opportunity.listUpdated}:room:${currentOrganization?.org_id}`,
 *   messageCallback: data => {
 *     console.log(data);
 *   },
 * });
 * ```
 */
export const useSocketConnection = <T>({
  room,
  event,
  onError,
  messageCallback,
  serviceName = SocketServiceEnum.ROOT_BACKEND_SERVICE,
}: SocketConnectionParams<T>) => {
  const eventCallback = useCallback(messageCallback, [messageCallback]);

  useEffect(() => {
    const socketKey = `${serviceName}-${event}-${room}`;
    let socket = socketInstances.get(socketKey);

    if (!socket) {
      socket = io(socketConfigs[serviceName].host, {
        path: "/socket.io/",
        reconnection: true,
        reconnectionAttempts: 5,
        reconnectionDelay: 1000,
        reconnectionDelayMax: 5000,
        timeout: 20000,
        transports: ["websocket", "polling"], // Try websocket first, fallback to polling
      });
      socketInstances.set(socketKey, socket);
    }

    const loadSocket = () => {
      if (socket.disconnected) {
        socket.connect();
      }

      socket.on("connect", () => {
        devLog("Socket connected");

        // Join a room if room is provided
        if (room) {
          socket.emit("join-room", room);
        }
      });

      socket.on("join-room", error => {
        devLog("join-room", error);
      });

      socket.on(event, (data: SocketEventData<T>) => {
        devLog("Socket event", data);
        eventCallback(data);
      });

      // Handle socket error type events
      socket.on("error", error => {
        devLog("Socket error", error);
        onError?.(error);
      });

      socket.on("reconnect_attempt", attemptNumber => {
        devLog(`Reconnection attempt ${attemptNumber}`);
      });

      socket.on("reconnect_failed", () => {
        devLog("Failed to reconnect after max attempts");
      });

      socket.on("disconnect", () => {
        devLog("Socket disconnected");
      });
    };

    loadSocket();

    return () => {
      socket.off("connect");
      socket.off("error");
      socket.off("disconnect");
      socket.off(event);

      if (
        socket.connected &&
        socketInstances.get(socketKey)?.listeners(event)?.length === 1
      ) {
        socket.disconnect();
        socketInstances.delete(socketKey);
      }
    };
  }, [serviceName, event, room, eventCallback, messageCallback, onError]);
};
